import { useState } from "react";
import { AlertTriangle, ExternalLink, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import Typography from "@/components/ui/typography";
import { getStripeAccountUpdateLink } from "@/features/invoices/api/invoices";

interface StripeRestrictedBannerProps {
  status: string;
  pendingVerification: boolean;
}

const StripeRestrictedBanner = ({
  status,
  pendingVerification,
}: StripeRestrictedBannerProps) => {
  const [isLoadingLink, setIsLoadingLink] = useState(false);

  const handleUpdateClick = async () => {
    setIsLoadingLink(true);
    try {
      const { url } = await getStripeAccountUpdateLink();
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      window.open("https://dashboard.stripe.com", "_blank", "noopener,noreferrer");
    } finally {
      setIsLoadingLink(false);
    }
  };

  let message = "Update your account to create invoices and enable payments from clients. Log in to your Stripe account to learn more.";
  if (status === "payouts_restricted") {
    message = "You can still create invoices, but payouts are paused until this is resolved.";
  } else if (status === "pending_verification") {
    message = "Your account is under review. You may not be able to create invoices or receive payments until verification is complete.";
  } else if (status === "platform_paused") {
    message = "Your account has been paused. Please contact support for assistance.";
  }

  return (
    <div className="bg-[#FFF7ED] rounded-2xl p-4 sm:p-6">
      <div className="flex items-start gap-4">
        <AlertTriangle className="h-5 w-5 text-[#EA580C] flex-shrink-0 mt-0.5" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 flex-1">
          <div className="space-y-0.5">
            <Typography size="l2" className="font-semibold text-[#494949]">
              Stripe Account Restricted
            </Typography>
            <Typography size="b2" className="text-[#767677]">
              {message}
            </Typography>
            {pendingVerification && (
              <div className="flex items-center gap-2 pt-2">
                <Loader2 className="h-3.5 w-3.5 text-[#2563EB] animate-spin flex-shrink-0" />
                <Typography size="b2" className="text-[#2563EB]">
                  Stripe is processing your information. This may take a few minutes.
                </Typography>
              </div>
            )}
          </div>
          <Button
            onClick={handleUpdateClick}
            disabled={isLoadingLink}
            className="bg-[#1e1e24] text-white hover:bg-[#333] whitespace-nowrap self-start sm:self-auto"
          >
            {isLoadingLink ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="mr-2 h-4 w-4" />
            )}
            Update on Stripe
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StripeRestrictedBanner;
